// Google Analytics Utility Functions

declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

const GA_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;

// Check if Google Analytics is enabled
export const isAnalyticsEnabled = !!GA_ID && GA_ID !== 'G-XXXXXXXXXX';

// Initialize Google Analytics
export const initGA = (measurementId: string = GA_ID): void => {
  if (!isAnalyticsEnabled || !measurementId) {
    console.warn('Google Analytics is not configured. Set VITE_GOOGLE_ANALYTICS_ID in .env');
    return;
  }

  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // We'll handle page views manually with React Router
  });
};

// Track page view
export const trackPageView = (path: string, title?: string): void => {
  if (!isAnalyticsEnabled) return;

  window.gtag('config', GA_ID, {
    page_path: path,
    page_title: title || document.title,
  });
};

// Track custom event
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number,
  additionalParams?: Record<string, any>
): void => {
  if (!isAnalyticsEnabled) return;

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
    value: value,
    ...additionalParams,
  });
};

// Predefined event tracking functions
export const trackDonate = (amount: number, currency: string = 'USD'): void => {
  trackEvent('donation', 'engagement', currency, amount, {
    currency: currency,
    value: amount,
  });
};

export const trackVolunteerSignup = (formSection: string): void => {
  trackEvent('volunteer_signup', 'engagement', formSection);
};

export const trackNewsletterSignup = (source: string): void => {
  trackEvent('newsletter_signup', 'engagement', source);
};

export const trackSocialShare = (platform: string): void => {
  trackEvent('share', 'engagement', platform);
};

export const trackButtonClick = (buttonName: string, location: string): void => {
  trackEvent('click', 'engagement', `${buttonName} - ${location}`);
};

export const trackVideoPlay = (videoTitle: string): void => {
  trackEvent('video_play', 'engagement', videoTitle);
};

export const trackFileDownload = (fileName: string, fileType: string): void => {
  trackEvent('download', 'engagement', fileName, undefined, {
    file_type: fileType,
  });
};

export const trackFormSubmission = (formName: string, success: boolean): void => {
  trackEvent('form_submit', 'engagement', formName, undefined, {
    success: success,
  });
};

export const trackNavigation = (from: string, to: string): void => {
  trackEvent('navigation', 'engagement', `From ${from} to ${to}`);
};
